import { MediaMatcher } from '@angular/cdk/layout';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { PrDurationPipe } from '@app/pipes/ea-duration.pipe';
import * as _ from 'lodash';
import moment from 'moment';
import { Observable } from 'rxjs';
import { SwiperOptions } from 'swiper';

const bulletStyles = 'width: 26px;height: 8px;border-radius: 6px; margin:0 4px';
export class PrTable {
  data: any;
  columns: PrTableColumn[];
  total?: PrTableColumn[];

  constructor(data: any, columns: PrTableColumn[], total?: PrTableColumn[]) {
    this.data = data;
    this.columns = columns;
    this.total = total;
  }
}

interface PrTableColumnOption {
  class?: string;
  style?: string;
  styleCol?: string;
  styleColInside?: string;
  styleTitle?: string;
  suffix?: string;
  colored?: boolean;
  prefix?: string;
  tooltip?: boolean;
  hasTotal?: boolean;
  totalText?: boolean;
  span?: 1 | 2 | 3 | 4 | 5 | 6;
  customTotalValue?: any;
  durationFutur?: boolean;
  value?(value: any): any;
  changeCallback?(value: any, oldValue: any, column: any): any;
  selectOptions?: any[];
  updateSelectOptions?: (value: any) => any[];
  selectOptionsAsync?: Observable<any[]>;
  disabled?(row: any): boolean;
}

export class PrTableColumn {
  label: string;
  name: string;
  type?: string;
  options?: PrTableColumnOption = {};

  public constructor(
    label: string,
    name: string | ((val: any) => void),
    options?: PrTableColumnOption,
  );
  public constructor(
    label: string,
    name: string | ((val: any) => void),
    type?: string,
    options?: PrTableColumnOption,
  );
  public constructor(...table: any[]) {
    this.label = table[0];
    this.name = table[1];
    if (table.length == 3)
      typeof table[2] == 'object'
        ? (this.options = table[2] || {})
        : (this.type = table[2]);
    else if (table.length == 4) {
      this.type = table[2];
      this.options = table[3];
    }

    if (typeof table[1] === 'function' && this.type != 'html')
      this.type = 'function';
  }
}

@Component({
    selector: 'pr-table',
    templateUrl: './pr-table.component.html',
    styleUrls: ['./pr-table.component.scss'],
    standalone: false
})
export class PrTableComponent implements OnInit {
  private _tableData?: PrTable;
  public get tableData(): PrTable {
    return this._tableData;
  }
  @Input() public set tableData(value: PrTable) {
    if (!value) return;
    this._tableData = value;
    this.sumTotal();
  }

  @Input() public set columnsDefinition(columns: PrTableColumn[]) {
    if (columns?.length)
      this.tableData = new PrTable(
        this.tableData ? this.tableData.data : [],
        columns,
      );
  }

  @Input() public set data(data: any[]) {
    if (data?.length)
      this.tableData = new PrTable(data, this.tableData.columns);
    else this.tableData = new PrTable([], this.tableData.columns);
  }

  @Input() clazz: string = '';
  @Input() showLoader: boolean = false;
  @Input() actionsTemplate: TemplateRef<any>;
  @Input() headerTitle: string;
  @Input() swiperMinWidth: number = 768;
  @Input() showTotalOnSingleRow: boolean = false;
  @Input() dense: boolean = false;
  @Input() set swiperConfig(value: SwiperOptions) {
    if (value) this.config = { ...this.config, ...value };
  }
  @Input() rowClass?: (row: any) => string;
  @Input() sortable: boolean = true;
  @Output() rowClick: EventEmitter<any> = new EventEmitter();
  @ViewChild(TemplateRef) header;
  @ViewChild('.pr-table-card-row') tableRow;
  sortColumn: any = {};
  total: any = {};
  today: Date = new Date();

  _ = _;

  mdq: MediaQueryList;
  mediaQueryListener: () => void;
  @ViewChild('mobileDisplay') mobileDisplay: TemplateRef<any>;
  @ViewChild('desktopDisplay') desktopDisplay: TemplateRef<any>;

  public config: SwiperOptions = {
    a11y: {
      enabled: true,
      prevSlideMessage: 'Carte précédente',
      nextSlideMessage: 'Carte suivante',
      firstSlideMessage: 'Première carte',
      lastSlideMessage: 'Dernière carte',
    },
    direction: 'horizontal',
    slidesPerView: 1.05,
    keyboard: true,
    mousewheel: true,
    pagination: {
      clickable: true,
      type: 'custom',
      renderCustom(swiper, current, total) {
        let out = '';
        for (let i = 1; i <= total; i++) {
          const isActive = i === current;
          out += `
            <span
              style="${bulletStyles}"
              class="swiper-pagination-bullet ${
                isActive ? 'swiper-pagination-bullet-active' : ''
              }"
              aria-hidden="true"
            ></span>
          `;
        }
        return out;
      },
    },

    touchStartPreventDefault: true,
    centeredSlides: false,
    spaceBetween: 10,
    slidesOffsetBefore: 10,
    slidesOffsetAfter: 10,
    threshold: 0,
    breakpoints: {
      760: {
        slidesPerView: 2.1,
        centeredSlides: false,
      },
    },
    watchSlidesProgress: true,
  };

  constructor(
    private media: MediaMatcher,
    public sanitizer: DomSanitizer,
    private prDurationPipe: PrDurationPipe,
  ) {}

  ngOnInit() {
    this.mdq = this.media.matchMedia(`(max-width: ${this.swiperMinWidth}px)`);
  }

  sort(column) {
    if (!this.sortable) return;
    if (this.sortColumn.name != column.name) {
      this.sortColumn = column;
      this.sortColumn.way = 'asc';
    } else
      this.sortColumn.way == 'asc'
        ? (this.sortColumn.way = 'desc')
        : (this.sortColumn.way = 'asc');
  }

  sortBy() {
    if (!this.tableData) return [];
    if (!this.sortable) return this.tableData.data;
    if (!this.sortColumn) this.sortColumn = this.tableData.columns[0];
    this.tableData.data = _.orderBy(
      this.tableData.data,
      data => {
        if (
          (this.sortColumn.name && Number(data[this.sortColumn.name])) ||
          this.sortColumn.type == 'number'
        )
          return Number(
            data[this.sortColumn.name]
              .toString()
              .replace(new RegExp('\\$[^1-9]', 'gm'), ''),
          );
        else if (this.sortColumn.name && this.sortColumn.type == 'date')
          return new Date(data[this.sortColumn.name]);
        else if (
          this.sortColumn.name &&
          typeof data[this.sortColumn.name] == 'string'
        )
          return data[this.sortColumn.name].toLowerCase();
        else return data;
      },
      this.sortColumn.way,
    );
    return this.tableData.data;
  }

  findNumberRegex = new RegExp(
    /(?<prefix>[^0-9]*)(?<number>[0-9]*)(?<suffix>[^0-9]*)/,
  );

  sumTotal() {
    if (!this.tableData.columns || this.tableData.columns.length == 0) return;
    this.total = {};
    this.total[this.tableData.columns[0].name] = 'Total';
    this.tableData.columns.forEach((col, i) => {
      if (i != 0 && col.options && col.options.hasTotal) {
        let total;
        if (col.options.hasOwnProperty('customTotalValue'))
          return (this.total[col.name] = col.options.customTotalValue);

        total = _(this.tableData.data)
          .map(data => {
            if (col.type == 'duration') {
              let duration = this.prDurationPipe.transform(
                data[col.name],
                col.options.durationFutur,
                true,
              ) as moment.Duration;
              return duration.asMonths();
            } else
              return isNaN(+data[col.name])
                ? +String(data[col.name]).match(this.findNumberRegex)['groups']
                    .number
                : +data[col.name];
          })
          .sum();

        if (col.type == 'duration')
          this.total[col.name] = this.prDurationPipe.getDiffDate(
            total,
            'M',
            col.options.durationFutur,
          );
        else this.total[col.name] = total;
      }
    });
  }

  get totalColumn() {
    return this.total || null;
  }

  get hasTotal() {
    return (
      (this.tableData &&
        this.tableData.data &&
        (this.tableData.data.length > 1 ||
          (this.showTotalOnSingleRow && this.tableData.data.length > 0)) &&
        this.tableData.columns &&
        this.tableData.columns.filter(c => c.options.hasTotal).length > 0) ||
      false
    );
  }

  inputChanged(newValue: any, row: PrTableColumn[], column: PrTableColumn) {
    if (typeof newValue == 'string') newValue = newValue.replace(',', '.');
    row[column.name] = newValue.value ? newValue.value : newValue;
    if (column.options?.changeCallback)
      column.options.changeCallback(row[column.name], row, column);
  }

  roundIfNumber(value) {
    return typeof value === 'number' ? Math.round(value * 100) / 100 : value;
  }

  getRowClass(row: any, isTotal: boolean): string {
    if (isTotal || !this.rowClass || !row) return '';
    return this.rowClass(row) || '';
  }

  getColumnClass(value: string): string {
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue) && typeof numericValue === 'number') {
      return numericValue > 0 ? 'colored' : 'colored-neg';
    }
    return '';
  }
}
